/**
 * airan-ntn — Channel Models
 * 3GPP TS 38.901 NTN channel models, Doppler, fading
 */

import { ChannelModel, Vec3, SimConfig, UserEquipment, Satellite } from './types.js';
import { EARTH_RADIUS, SPEED_OF_LIGHT, CHANNEL_DEFAULTS, FREQUENCY_BANDS } from './config.js';
import { vec3Len, vec3Sub, vec3Norm, vec3Dot, vec3Scale, vec3Add } from './types.js';

// ──────────────────────────────────────────────────────────────────────────────
// RNG Helpers
// ──────────────────────────────────────────────────────────────────────────────

let rngState = 42;

export function setSeed(seed: number): void {
  rngState = seed;
}

export function random(): number {
  // Park-Miller LCG
  rngState = (rngState * 16807) % 2147483647;
  return (rngState - 1) / 2147483646;
}

export function randn(): number {
  // Box-Muller transform
  const u = 1 - random();
  const v = random();
  return Math.sqrt(-2 * Math.log(u)) * Math.cos(2 * Math.PI * v);
}

function randnPair(): [number, number] {
  const u = 1 - random();
  const v = random();
  const r = Math.sqrt(-2 * Math.log(u));
  const t = 2 * Math.PI * v;
  return [r * Math.cos(t), r * Math.sin(t)];
}

// ──────────────────────────────────────────────────────────────────────────────
// 3GPP TS 38.901 Path Loss (NTN)
// ──────────────────────────────────────────────────────────────────────────────

/**
 * Path loss calculation per 3GPP TS 38.901
 * NTN UMa LOS: PL = 32.4 + 20*log10(d) + 20*log10(f) + X_sigma
 * NTN UMa NLOS: PL = 32.4 + 36.7*log10(d) + 20*log10(f) + X_sigma
 */
export function pathLoss(
  uePos: Vec3,
  satPos: Vec3,
  config: SimConfig,
  elevation: number
): number {
  const dx = uePos.x - satPos.x;
  const dy = uePos.y - satPos.y;
  const dz = uePos.z - satPos.z;
  const distKm = Math.sqrt(dx*dx + dy*dy + dz*dz) / 1000; // km

  // Slant range
  const d = Math.max(distKm, 0.001);

  let slope: number;
  let shadowingStd: number;

  switch (config.channelModel) {
    case 'NTN-LOS':
      slope = 20; // Free space exponent
      shadowingStd = CHANNEL_DEFAULTS.ntnLos.shadowingStd;
      break;
    case 'NTN-NLOS':
      slope = 36.7;
      shadowingStd = CHANNEL_DEFAULTS.ntnNlos.shadowingStd;
      break;
    case 'Rayleigh':
    case 'Rician':
    case 'AWGN':
      // Terrestrial models
      slope = config.pathLossExponent > 0 ? 10 * config.pathLossExponent : 20;
      shadowingStd = config.shadowingStd;
      break;
    default:
      slope = 20;
      shadowingStd = 4;
  }

  // 3GPP TS 38.901: PL = 32.4 + 10*n*log10(d) + 20*log10(f) + X_sigma
  const fspl = 32.4 + slope * Math.log10(d) + 20 * Math.log10(config.carrierFreqGHz);
  const shadowing = randn() * shadowingStd;

  return fspl + shadowing;
}

// ──────────────────────────────────────────────────────────────────────────────
// Doppler Shift
// ──────────────────────────────────────────────────────────────────────────────

/**
 * Doppler shift for satellite-UE link
 * f_d = (v_rel / c) * f_c
 * v_rel = relative velocity along line-of-sight
 */
export function dopplerShift(
  uePos: Vec3,
  ueVel: Vec3,
  satPos: Vec3,
  satVel: Vec3,
  carrierFreqHz: number
): number {
  const los = vec3Norm(vec3Sub(satPos, uePos));
  const relVel = vec3Sub(satVel, ueVel);
  const vRel = vec3Dot(relVel, los);
  return (vRel / SPEED_OF_LIGHT) * carrierFreqHz;
}

export function dopplerSpread(
  uePos: Vec3,
  ueVel: Vec3,
  satPos: Vec3,
  satVel: Vec3,
  carrierFreqHz: number,
  numPaths: number = 5
): number {
  // Multipath Doppler spread (simplified)
  let totalSpread = 0;
  for (let i = 0; i < numPaths; i++) {
    // Random scatterer offset
    const scatterer = vec3Add(uePos, {
      x: (random() - 0.5) * 100,
      y: (random() - 0.5) * 100,
      z: (random() - 0.5) * 10
    });
    const spread = dopplerShift(uePos, ueVel, scatterer, satVel, carrierFreqHz);
    totalSpread += Math.abs(spread - dopplerShift(uePos, ueVel, satPos, satVel, carrierFreqHz));
  }
  return totalSpread / numPaths;
}

// ──────────────────────────────────────────────────────────────────────────────
// Fading Models
// ──────────────────────────────────────────────────────────────────────────────

export function fadingGain(model: ChannelModel, ricianK: number): number {
  switch (model) {
    case 'AWGN':
      return 1.0;

    case 'Rayleigh': {
      const [re, im] = randnPair();
      return (re * re + im * im) / 2; // Exponential(1)
    }

    case 'Rician': {
      const K = Math.max(ricianK, 0);
      const sigma2 = 1 / (2 * (K + 1));
      const nu = Math.sqrt(K / (K + 1));
      const [re, im] = randnPair();
      const r = Math.sqrt((re * Math.sqrt(sigma2) + nu) ** 2 + (im * Math.sqrt(sigma2)) ** 2);
      return r * r;
    }

    case 'NTN-LOS': {
      // Strong LOS component
      const K = Math.pow(10, CHANNEL_DEFAULTS.ntnLos.riceKFactor / 10);
      const sigma2 = 1 / (2 * (K + 1));
      const nu = Math.sqrt(K / (K + 1));
      const [re, im] = randnPair();
      const r = Math.sqrt((re * Math.sqrt(sigma2) + nu) ** 2 + (im * Math.sqrt(sigma2)) ** 2);
      return r * r;
    }

    case 'NTN-NLOS': {
      // Rayleigh-like but with some clustering
      const [re, im] = randnPair();
      return (re * re + im * im) / 2;
    }

    default:
      return 1.0;
  }
}

// ──────────────────────────────────────────────────────────────────────────────
// SNR Calculation
// ──────────────────────────────────────────────────────────────────────────────

export function computeSNR(
  ueTxPower: number,    // dBm
  pathLossDb: number,   // dB
  gain: number,         // Linear |h|^2
  noisePower: number    // dBm
): number {
  const gainDb = 10 * Math.log10(Math.max(gain, 1e-12));
  return ueTxPower - pathLossDb + gainDb - noisePower;
}

export function noisePowerdBm(bandwidthMHz: number): number {
  return -174 + 10 * Math.log10(bandwidthMHz * 1e6);
}

// ──────────────────────────────────────────────────────────────────────────────
// Shannon Capacity & Communication Cost
// ──────────────────────────────────────────────────────────────────────────────

export function shannonCapacity(snrDb: number): number {
  const snrLin = Math.pow(10, snrDb / 10);
  return Math.log2(1 + snrLin);
}

export function commCostBits(modelParamCount: number, snrDb: number): number {
  const bitsPerParam = 32; // FP32
  const rawBits = modelParamCount * bitsPerParam;
  const capacity = shannonCapacity(snrDb);
  const efficiency = Math.min(1, capacity / 6); // Cap at 6 bpHz for overhead
  return rawBits / Math.max(efficiency, 0.01);
}

// ──────────────────────────────────────────────────────────────────────────────
// Beamforming
// ──────────────────────────────────────────────────────────────────────────────

export function beamformingGain(theta: number, targetTheta: number, M: number): number {
  const dTheta = theta - targetTheta;
  if (Math.abs(dTheta) < 1e-9) return M * M;
  const x = Math.PI * Math.sin(dTheta);
  const sinc = Math.sin(M * x) / (M * Math.sin(x));
  return sinc * sinc;
}

export function steeringVector(theta: number, M: number): number[] {
  const v: number[] = [];
  for (let m = 0; m < M; m++) {
    v.push(Math.cos(m * Math.PI * Math.sin(theta)));
    v.push(Math.sin(m * Math.PI * Math.sin(theta)));
  }
  return v; // Length 2M (real+imag interleaved)
}

export function azimuth(uePos: Vec3, satPos: Vec3): number {
  const dx = satPos.x - uePos.x;
  const dy = satPos.y - uePos.y;
  return Math.atan2(dy, dx);
}

// ──────────────────────────────────────────────────────────────────────────────
// Channel Refresh (per round)
// ──────────────────────────────────────────────────────────────────────────────

export interface ChannelState {
  pathLoss: number;
  gain: number;
  snr: number;
  doppler: number;
  elevation: number;
}

export function refreshChannels(
  ues: import('./types.js').UserEquipment[],
  satellites: import('./types.js').Satellite[],
  config: SimConfig,
  roundDuration: number
): void {
  for (const ue of ues) {
    if (ue.servingSatId === null) continue;
    const sat = satellites.find(s => s.id === ue.servingSatId);
    if (!sat) continue;

    const elevation = calculateElevation(ue.position, sat.position);
    const pl = pathLoss(ue.position, sat.position, config, elevation);
    const gain = fadingGain(config.channelModel, config.ricianK);
    const noise = noisePowerdBm(config.bandwidthMHz);
    const snr = computeSNR(ue.txPower, pl, gain, noise);

    let doppler = 0;
    if (config.dopplerEnabled) {
      doppler = dopplerShift(ue.position, ue.velocity, sat.position, sat.velocity, config.carrierFreqGHz * 1e9);
    }

    ue.pathLoss = pl;
    ue.channelGain = gain;
    ue.snr = snr;
    ue.sinr = snr; // Same for now
    ue.dopplerShift = doppler;
    ue.bsId = sat.id;
    ue.angle = azimuth(ue.position, sat.position);
  }
}

function calculateElevation(uePos: Vec3, satPos: Vec3): number {
  const ueToSat = vec3Norm(vec3Sub(satPos, uePos));
  const ueUp = vec3Norm(uePos);
  const elevationRad = Math.asin(Math.max(-1, Math.min(1, vec3Dot(ueToSat, ueUp))));
  return elevationRad * 180 / Math.PI;
}

// ──────────────────────────────────────────────────────────────────────────────
// Inter-satellite Link (ISL) Channel
// ──────────────────────────────────────────────────────────────────────────────

export function islChannel(
  satA: import('./types.js').Satellite,
  satB: import('./types.js').Satellite,
  freqGHz: number,
  bandwidthMHz: number
): { snr: number; capacity: number; latency: number } {
  const dist = vec3Len(vec3Sub(satA.position, satB.position));
  const latency = dist / SPEED_OF_LIGHT;

  // Free space path loss
  const fspl = 92.45 + 20 * Math.log10(freqGHz) + 20 * Math.log10(dist / 1000);

  // Laser: ~1550 nm, ~193 THz, extremely narrow beam
  // RF: Ka-band, wider beam
  const txPower = 20; // dBm
  const rxGain = 30;  // dBi
  const txGain = 30;  // dBi
  const noise = -174 + 10 * Math.log10(bandwidthMHz * 1e6) + 3;

  const rxPower = txPower + txGain + rxGain - fspl;
  const snr = rxPower - noise;
  const capacity = shannonCapacity(snr) * bandwidthMHz * 1e6;

  return { snr, capacity, latency };
}

// ──────────────────────────────────────────────────────────────────────────────
// Feeder Link Channel
// ──────────────────────────────────────────────────────────────────────────────

export function feederLinkChannel(
  sat: import('./types.js').Satellite,
  gs: import('./types.js').GroundStation,
  freqGHz: number,
  bandwidthMHz: number
): { snr: number; capacity: number; latency: number; elevation: number } {
  const slantRange = vec3Len(vec3Sub(sat.position, gs.position));
  const latency = slantRange / SPEED_OF_LIGHT;

  // Elevation from ground station
  const gsToSat = vec3Norm(vec3Sub(sat.position, gs.position));
  const gsUp = vec3Norm(gs.position);
  const elevation = Math.asin(Math.max(-1, Math.min(1, vec3Dot(gsToSat, gsUp)))) * 180 / Math.PI;

  // Path loss
  const fspl = 92.45 + 20 * Math.log10(freqGHz) + 20 * Math.log10(slantRange / 1000);
  const atmLoss = 0.5 / Math.sin(Math.max(0.01, elevation * Math.PI / 180));

  const txPower = 30; // dBm (GS)
  const gsGain = 50;  // dBi
  const satGain = 40; // dBi
  const noise = -174 + 10 * Math.log10(bandwidthMHz * 1e6) + 3;

  const rxPower = txPower + gsGain + satGain - fspl - atmLoss;
  const snr = rxPower - noise;
  const capacity = shannonCapacity(snr) * bandwidthMHz * 1e6;

  return { snr, capacity, latency, elevation };
}